import { Button } from '@sweep-io/sweep-design';
import { KeyValueTableRow } from './KeyValueTableRow';
import { Box } from '@mui/material';
import { memo, useCallback, useMemo } from 'react';

interface KeyValueTableProps {
  readonly?: boolean;
  params: KeyValueStringTemplate[];
  onChange: (params: KeyValueStringTemplate[]) => any;
  crmOrgId: string;
  objectName: string;
  keyString: string;
}

export const KeyValueTable = memo(
  ({ readonly, params, onChange, crmOrgId, objectName, keyString }: KeyValueTableProps) => {
    const isHideAddNew = useMemo(
      () =>
        params?.length === 0 ||
        (params?.length === 1 && params?.[0]?.key === '' && params?.[0]?.value?.template === ''),
      [params],
    );

    const emptyObject = useMemo(
      () => ({
        key: '',
        value: { template: '', variables: [] },
      }),
      [],
    );

    const tempParams = useMemo(
      () => (params?.length === 0 ? [emptyObject] : params),
      [params, emptyObject],
    );

    const handleRowChange = useCallback(
      (idx: number, field: KeyValueStringTemplate) => {
        const newFields = [...(params || [])];
        if (newFields) {
          newFields[idx] = field;
          onChange(newFields);
        }
      },
      [params, onChange],
    );

    const handleRowDelete = useCallback(
      (idx: number) => {
        const tempFields = [...(params || [])];
        tempFields.splice(idx, 1);
        onChange(tempFields);
      },
      [params, onChange],
    );

    const handleAddRow = useCallback(() => {
      let tempFields = params || [];
      tempFields = [...tempFields, emptyObject];
      onChange(tempFields);
    }, [params, onChange, emptyObject]);

    return (
      <>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flexWrap: 'nowrap',
            width: '100%',
            '& .key-value-row:first-of-type fieldset': {
              borderTopLeftRadius: '4px',
            },
            '& .key-value-row:last-of-type fieldset': {
              borderBottomWidth: '1px',
              borderBottomLeftRadius: '4px',
            },
            '& .key-value-row:first-of-type .quill .ql-editor': {
              borderTopRightRadius: '4px',
            },
            '& .key-value-row:last-of-type .quill .ql-editor': {
              borderBottomWidth: '1px',
              borderBottomRightRadius: '4px',
            },
          }}
        >
          {tempParams?.map((set, idx) => (
            <KeyValueTableRow
              readonly={readonly}
              objectName={objectName}
              crmOrgId={crmOrgId}
              key={keyString + idx}
              keyValueSet={set}
              onChange={(field) => handleRowChange(idx, field)}
              onDelete={() => handleRowDelete(idx)}
            />
          ))}
        </Box>
        <Box>
          <Button
            size="medium"
            disabled={isHideAddNew}
            onClick={handleAddRow}
            startIconName="Plus"
            variant="flat-blue"
          >
            Add row
          </Button>
        </Box>
      </>
    );
  },
);

KeyValueTable.displayName = 'KeyValueTable';
